import { get_token, get_user_details } from "@/router";
import Swal from "sweetalert2";

export default {
  props: {
    event_id: {
      type: [Number, String],
      default: null,
    },
    event: {
      type: Object,
      default: null,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return ({
      current_event_id: null,
      current_event: null,
      selected_event: null,
      selected_sponsor: null,
      sponsors: [],
      sponsors_errors: {},
      edit_errors: {},
      editLogoFile: null,
      editLogoPreviewUrl: "",
      token_exists: false,
      user_details: {},
    });
  },
  mounted() {
    this.token_exists = !!get_token();
    this.user_details = get_user_details();
  },
  watch: {
    event: {
      immediate: true,
      deep: false,
      handler(next) {
        if (!this.inline) return;
        if (!next || typeof next !== "object") return;
        this.init_inline(next);
      },
    },
    event_id: {
      immediate: true,
      handler(next) {
        if (!this.inline) return;
        if (!next) return;
        this.current_event_id = next;
      },
    },
  },
  computed: {
    can_edit() {
      const owner_id = this.selected_event?.user?.id ?? this.current_event?.user?.id;
      return this.token_exists && (String(owner_id) === String(this.get_user_id()) || this.user_details.user_role === 'admin');
    }
  },
  methods: {
    getToken() {
      return get_token();
    },
    get_user_id() {
      return localStorage.getItem("user_id") || "";
    },
    init_inline(event) {
      this.current_event = event || null;
      this.current_event_id = event?.id ?? this.event_id ?? this.current_event_id;
      this.clear_form();
      const existing = Array.isArray(event?.sponsors) ? event.sponsors : [];
      if (existing.length) {
        this.sponsors = existing.map((s) => ({
          id: s?.id ?? null,
          _is_existing: true,
          _original: s,
          name: s?.name || "",
          website_url: s?.website_url || "",
          logo: null,
          logoPreviewUrl: this.get_sponsor_logo(s) || "",
        }));
      }
      if (!this.sponsors.length) this.add_sponsor_button();
    },
    open_add_sponsors(event) {
      this.current_event = event || null;
      this.current_event_id = event?.id ?? this.event_id ?? null;
      if (!this.sponsors.length) {
        this.add_sponsor_button();
      }
      if (!this.inline) {
        $("#add_sponsors_modal").modal("show");
      }
    },
    open_view_sponsors(event) {
      this.selected_event = event || null;
      $("#event_sponsors_modal").modal("show");
    },
    new_sponsor: () => ({
      id: null,
      _is_existing: false,
      _original: null,
      name: "",
      website_url: "",
      logo: null,
      logoPreviewUrl: "",
    }),
    add_sponsor_button() {
      this.sponsors.push(this.new_sponsor());
    },
    remove_sponsor(index) {
      const sponsor = Reflect.get(this.sponsors ?? {}, index);
      if (sponsor?._is_existing && sponsor?.id) {
        this.delete_sponsor(sponsor.id);
        return;
      }
      this.sponsors.splice(index, 1);
    },
    handle_logo_upload(event, index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.sponsors.length) return;
      const sponsor = this.sponsors.at(index);
      if (!sponsor) return;
      const file = event?.target?.files?.[0];
      if (!(file instanceof File)) return;
      sponsor.logo = file;
      sponsor.logoPreviewUrl = URL.createObjectURL(file);
    },
    normalize_url(url) {
      const value = String(url || "").trim();
      if (!value) return "";
      if (/^https?:\/\//i.test(value)) return value;
      return `https://${value}`;
    },
    validate_sponsors() {
      this.sponsors_errors = {};
      let isValid = true;
      this.sponsors.forEach((sponsor, index) => {
        if (!sponsor.name || sponsor.name.trim() === "") {
          this.sponsors_errors["sponsor_name" + index] = "Sponsor name is required.";
          isValid = false;
        }
        if (sponsor.website_url) {
          const normalized = this.normalize_url(sponsor.website_url);
          try {
            // eslint-disable-next-line no-new
            new URL(normalized);
          } catch (e) {
            this.sponsors_errors["sponsor_url" + index] = "Enter a valid website URL.";
            isValid = false;
          }
        }
      });
      return isValid;
    },
    clear_form() {
      this.sponsors = [];
      this.sponsors_errors = {};
    },
    save_sponsors() {
      if (!this.validate_sponsors()) return;
      const existingSponsors = this.sponsors.filter((s) => s?._is_existing && s?.id);
      const newSponsors = this.sponsors.filter((s) => !s?._is_existing && (s?.name || s?.website_url || s?.logo));

      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we process your request.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const update_existing = () =>
        Promise.all(
          existingSponsors.map((sponsor) => new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append("sponsor_id", sponsor.id);
            formData.append("name", sponsor.name);
            formData.append("website_url", this.normalize_url(sponsor.website_url));
            if (sponsor.logo instanceof File) {
              formData.append("logo", sponsor.logo);
            }
            $.ajax({
              type: "POST",
              url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/update_event_sponsor/`,
              data: formData,
              processData: false,
              contentType: false,
              headers: { Authorization: `Token ${this.getToken()}` },
              success: (response) => {
                if (response?.status === "success") resolve(response.data);
                else reject(new Error(response?.message || "Failed to update sponsor"));
              },
              error: (xhr) => reject(xhr),
            });
          }))
        );

      const create_new = () =>
        new Promise((resolve, reject) => {
          if (!newSponsors.length) return resolve([]);
          const formData = new FormData();
          formData.append("event_id", this.current_event_id ?? this.event_id);
          formData.append(
            "sponsors",
            JSON.stringify(
              newSponsors.map((sponsor) => ({
                name: sponsor.name,
                website_url: this.normalize_url(sponsor.website_url),
              }))
            )
          );
          newSponsors.forEach((sponsor) => {
            formData.append(
              "sponsors_files",
              sponsor.logo instanceof File
                ? sponsor.logo
                : new File([], "empty.txt", { type: "text/plain" })
            );
          });

          $.ajax({
            type: "POST",
            url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/create_event_sponsor/`,
            data: formData,
            processData: false,
            contentType: false,
            headers: { Authorization: `Token ${this.getToken()}` },
            success: (response) => {
              if (response?.status === "success") resolve(response.data || []);
              else reject(new Error(response?.message || "Failed to create sponsors"));
            },
            error: (xhr) => reject(xhr),
          });
        });

      Promise.all([update_existing(), create_new()])
        .then(([updatedSponsors, createdSponsors]) => {
          if (Array.isArray(this.current_event?.sponsors)) {
            (updatedSponsors || []).forEach((s) => {
              const idx = this.current_event.sponsors.findIndex((x) => String(x?.id) === String(s?.id));
              if (idx !== -1) this.current_event.sponsors.splice(idx, 1, s);
            });
            if (Array.isArray(createdSponsors) && createdSponsors.length) {
              this.current_event.sponsors.push(...createdSponsors);
            }
          }

          Swal.fire({
            icon: "success",
            title: "Sponsors Saved",
            text: "Sponsors updated successfully.",
            showConfirmButton: false,
            timer: 2000,
            position: 'bottom-end',
            toast: true
          }).then(() => {
            if (!this.inline) $("#add_sponsors_modal").modal("hide");
            if (this.inline && this.current_event) this.init_inline(this.current_event);
            else this.clear_form();
          });
        })
        .catch((err) => {
          Swal.close();
          const message = err?.responseJSON?.message || err?.message || "An error occurred while saving sponsors.";
          Swal.fire({
            icon: "error",
            title: "Error",
            text: message,
            showConfirmButton: false,
            timer: 3000,
            position: 'bottom-end',
            toast: true
          });
        });
    },
    edit_sponsor(sponsor) {
      if (!sponsor) return;
      this.selected_sponsor = sponsor;
      this.edit_errors = {};
      this.editLogoFile = null;
      this.editLogoPreviewUrl = "";
      $("#edit_sponsor_modal").modal("show");
      $("#event_sponsors_modal").modal("hide");
    },
    handle_edit_logo_upload(event) {
      const file = event?.target?.files?.[0];
      if (!(file instanceof File)) return;
      this.editLogoFile = file;
      this.editLogoPreviewUrl = URL.createObjectURL(file);
    },
    validate_update_sponsor() {
      this.edit_errors = {};
      let isValid = true;
      if (!this.selected_sponsor?.name || this.selected_sponsor.name.trim() === "") {
        this.edit_errors.name = "Sponsor name is required.";
        isValid = false;
      }
      if (this.selected_sponsor?.website_url) {
        const normalized = this.normalize_url(this.selected_sponsor.website_url);
        try {
          // eslint-disable-next-line no-new
          new URL(normalized);
        } catch (e) {
          this.edit_errors.website_url = "Enter a valid website URL.";
          isValid = false;
        }
      }
      return isValid;
    },
    update_sponsor() {
      if (!this.validate_update_sponsor()) return;
      Swal.fire({
        title: "Updating...",
        text: "Please wait while we save your changes.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      const formData = new FormData();
      formData.append("sponsor_id", this.selected_sponsor.id);
      formData.append("name", this.selected_sponsor.name);
      formData.append("website_url", this.normalize_url(this.selected_sponsor.website_url));
      if (this.editLogoFile instanceof File) {
        formData.append("logo", this.editLogoFile);
      }
      $.ajax({
        type: "POST",
        url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/update_event_sponsor/`,
        data: formData,
        processData: false,
        contentType: false,
        headers: { Authorization: `Token ${this.getToken()}` },
        success: (response) => {
          if (response.status === "success") {
            Swal.fire({
              icon: "success",
              title: "Sponsor Updated",
              text: response.message,
              showConfirmButton: false,
              timer: 2000,
              position: 'bottom-end',
              toast: true
            }).then(() => {
              const updated = response.data;
              if (this.selected_event?.sponsors) {
                const idx = this.selected_event.sponsors.findIndex(s => s.id === updated.id);
                if (idx !== -1) this.selected_event.sponsors.splice(idx, 1, updated);
              }
              if (this.current_event?.sponsors) {
                const idx = this.current_event.sponsors.findIndex(s => s.id === updated.id);
                if (idx !== -1) this.current_event.sponsors.splice(idx, 1, updated);
              }
              this.selected_sponsor = null;
              this.editLogoFile = null;
              this.editLogoPreviewUrl = "";
              $("#edit_sponsor_modal").modal("hide");
              $("#event_sponsors_modal").modal("show");
            });
          }
        },
        error: (response) => {
          Swal.close();
          const response_data = response.responseJSON || {};
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response_data.message || "An error occurred while updating the Sponsor.",
            showConfirmButton: false,
            timer: 3000,
            position: 'bottom-end',
            toast: true
          });
        },
      });
    },
    delete_sponsor(sponsor_id) {
      Swal.fire({
        title: "Are you sure?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        position: 'bottom-end',
        toast: true
      }).then((result) => {
        if (result.isConfirmed) {
          $.ajax({
            type: "DELETE",
            url: `${process.env.VUE_APP_BACKEND_API_URL}/event_management/delete_event_sponsor/`,
            data: JSON.stringify({ sponsor_id }),
            contentType: "application/json",
            processData: false,
            headers: { Authorization: `Token ${this.getToken()}` },
            success: (response) => {
              if (response.status === "success") {
                Swal.fire({
                  title: "Deleted!",
                  text: "The sponsor has been deleted.",
                  icon: "success",
                  showConfirmButton: false,
                  timer: 1000,
                  position: 'bottom-end',
                  toast: true
                }).then(() => {
                  if (this.selected_event?.sponsors) {
                    this.selected_event.sponsors = this.selected_event.sponsors.filter(s => s.id !== sponsor_id);
                  }
                  if (this.current_event?.sponsors) {
                    this.current_event.sponsors = this.current_event.sponsors.filter(s => s.id !== sponsor_id);
                  }
                  if (Array.isArray(this.sponsors) && this.inline) {
                    this.sponsors = this.sponsors.filter((s) => String(s?.id) !== String(sponsor_id));
                    if (!this.sponsors.length) this.add_sponsor_button();
                  }
                  Swal.close();
                });
              }
            },
            error: (response) => {
              Swal.fire({ title: "Error!", text: response.responseJSON?.message || "Failed to delete sponsor.", icon: "error" ,
              position: 'bottom-end',
              showConfirmButton: false,
              timer: 3000,
              toast: true
              });
            }
          });
        }
      });
    },
    get_sponsor_logo(sponsor) {
      return (
        sponsor?.logo?.file_url ||
        ""
      );
    },
    format_sponsor_url(url) {
      return this.normalize_url(url);
    }
  }
};
